/**
 * Gift card actions replicating reware-admin's Gift cards portal, which is
 * read-only apart from Resend gift card email (enabled only for ISSUED cards
 * via canResendGiftcardEmail). Number/PIN reveal lives inline in the Gift Card
 * section. Both hit the same admin_graphql calls the admin portal uses.
 */
import { useState } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import { Button, Stack, Tooltip } from '@mui/material';

import { deliveryAddressOf } from './GiftcardDetailSections';
import { resendGiftcardEmail } from './mutations';
import type { GiftcardRecord } from './types';

export const GiftcardActions = ({ gc }: { gc: GiftcardRecord }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);

  const address = deliveryAddressOf(gc);

  // Separate labels rather than `${label} failed`, which turned the success phrasing into
  // "Gift card email resent failed".
  const run = async (
    labels: { success: string, failure: string },
    action: () => Promise<unknown>
  ) => {
    setBusy(true);
    try {
      await action();
      notify(labels.success, { type: 'success' });
      refresh();
    } catch (error) {
      // Falls back on an empty message too: an HttpError built from a missing status text is
      // still an Error, so `instanceof` alone would notify ''.
      const message = error instanceof Error && error.message ? error.message : labels.failure;
      console.error('Gift card action failed', { giftcardId: gc.id, error });
      notify(message, { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  // Both gates surface as a disabled button with a reason rather than a missing
  // one, so the absence of the action is never unexplained.
  const resendReason = !address?.email ?
    'No email address on the associated order or trade-in' :
    !gc.canResendGiftcardEmail ?
      'Only issued gift cards can be resent' :
      null;

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
      {/* MUI can't attach a tooltip to a disabled button (pointer-events: none),
          hence the span shim — without it the reason never renders. */}
      <Tooltip title={resendReason ?? ''}>
        <span>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={busy || !!resendReason}
            onClick={() => run(
              {
                success: 'Gift card email resent',
                failure: 'Could not resend the gift card email',
              },
              () => resendGiftcardEmail(gc.id)
            )}
          >
            Resend Email
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );
};

export default GiftcardActions;
